import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VList } from 'virtua';
import { DictionaryType, DictionaryEntry } from '../../types';
import {
  getAllEntriesByType,
  deleteSingleEntry,
  batchDeleteEntriesFast,
  upsertSingleEntry,
  updateMetadata,
  countEntriesByType,
} from '../../services/db';
import { EntryModal } from './EntryModal';
import { useToast } from '../../context/ToastContext';
import { ArrowLeft, Search, Plus, Trash2, Edit2, AlertCircle, Loader2, CheckSquare, Square, Zap } from 'lucide-react';

interface DetailViewProps {
  dictType: DictionaryType;
  onBack: () => void;
}

export const DetailView: React.FC<DetailViewProps> = ({ dictType, onBack }) => {
  const toast = useToast();
  const [totalWordCount, setTotalWordCount] = useState<number>(0);
  const [displayedEntries, setDisplayedEntries] = useState<DictionaryEntry[]>([]);
  const [totalMatched, setTotalMatched] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTimeMs, setSearchTimeMs] = useState<number | null>(null);

  // Search Controls
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [isCaseSensitive, setIsCaseSensitive] = useState<boolean>(false);
  const [isRegex, setIsRegex] = useState<boolean>(false);
  const [regexError, setRegexError] = useState<string>('');

  // Selection / Bulk Delete State
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState<boolean>(false);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingEntry, setEditingEntry] = useState<DictionaryEntry | null>(null);

  // Web Worker Ref & Timing
  const workerRef = useRef<Worker | null>(null);
  const searchStartTimeRef = useRef<number>(0);

  // Initialize Worker & Load Data
  useEffect(() => {
    const worker = new Worker(
      new URL('../../workers/dictionary.worker.ts', import.meta.url),
      { type: 'module' }
    );
    workerRef.current = worker;

    worker.onmessage = (event) => {
      const data = event.data;

      if (data.type === 'INIT_CACHE_RESULT') {
        setTotalWordCount(data.totalLoaded);
        searchStartTimeRef.current = performance.now();
        worker.postMessage({
          type: 'SEARCH',
          dictType,
          query: '',
          isRegex: false,
          isCaseSensitive: false,
          page: 1,
          pageSize: 300,
        });
      } else if (data.type === 'SEARCH_RESULT') {
        const elapsed = Math.round(performance.now() - searchStartTimeRef.current);
        setSearchTimeMs(elapsed);
        setLoading(false);
        setRegexError('');
        if (data.page === 1) {
          setDisplayedEntries(data.results);
        } else {
          setDisplayedEntries((prev) => [...prev, ...data.results]);
        }
        setTotalMatched(data.totalMatched);
        setHasMore(data.hasMore);
        setCurrentPage(data.page);
      }
    };

    const loadData = async () => {
      try {
        setLoading(true);
        const data = await getAllEntriesByType(dictType);
        setTotalWordCount(data.length);

        worker.postMessage({
          type: 'INIT_CACHE',
          dictType,
          entries: data,
        });
      } catch (err) {
        console.error('Lỗi khi tải dữ liệu từ IndexedDB:', err);
        setLoading(false);
      }
    };

    loadData();

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, [dictType]);

  // Debounce search query 300ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Dispatch search to Web Worker
  useEffect(() => {
    if (!workerRef.current) return;
    setLoading(true);
    setSelectedKeys(new Set());

    searchStartTimeRef.current = performance.now();
    workerRef.current.postMessage({
      type: 'SEARCH',
      dictType,
      query: debouncedQuery,
      isRegex,
      isCaseSensitive,
      page: 1,
      pageSize: 300,
    });
  }, [dictType, debouncedQuery, isRegex, isCaseSensitive]);

  // Load more items (Infinite Scroll Trigger)
  const handleLoadMore = useCallback(() => {
    if (!hasMore || loading || !workerRef.current) return;

    workerRef.current.postMessage({
      type: 'LOAD_MORE_RESULTS',
      page: currentPage + 1,
      pageSize: 300,
    });
  }, [hasMore, loading, currentPage]);

  // Direct Single Entry Deletion (Instant Toast, NO confirm popup)
  const handleDeleteEntry = async (key: string) => {
    try {
      await deleteSingleEntry(dictType, key);
      const updatedCount = await countEntriesByType(dictType);
      await updateMetadata({
        type: dictType,
        wordCount: updatedCount,
        updatedAt: Date.now(),
      });

      setTotalWordCount(updatedCount);
      setDisplayedEntries((prev) => prev.filter((item) => item.key !== key));
      setTotalMatched((prev) => Math.max(0, prev - 1));
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });

      toast.success(`Đã xóa từ "${key}"!`);

      if (workerRef.current) {
        workerRef.current.postMessage({
          type: 'SYNC_INCREMENTAL',
          dictType,
          action: 'delete',
          entryKey: key,
        });
      }
    } catch (err: any) {
      toast.error(`Xóa thất bại: ${err.message || 'Lỗi không xác định'}`);
    }
  };

  // Toggle selection for a single item
  const toggleSelectKey = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Toggle Select All / Deselect All
  const handleToggleSelectAll = () => {
    if (selectedKeys.size >= displayedEntries.length && displayedEntries.length > 0) {
      setSelectedKeys(new Set());
    } else {
      const allKeys = new Set(displayedEntries.map((item) => item.key));
      setSelectedKeys(allKeys);
    }
  };

  // Execute Bulk Delete (Xóa hàng loạt)
  const handleBulkDelete = async () => {
    const keysArray = Array.from(selectedKeys);
    if (keysArray.length === 0) return;

    try {
      setIsBulkDeleting(true);
      await batchDeleteEntriesFast(dictType, keysArray);

      const updatedCount = await countEntriesByType(dictType);
      await updateMetadata({
        type: dictType,
        wordCount: updatedCount,
        updatedAt: Date.now(),
      });

      const keySet = new Set(keysArray);
      setTotalWordCount(updatedCount);
      setDisplayedEntries((prev) => prev.filter((item) => !keySet.has(item.key)));
      setTotalMatched((prev) => Math.max(0, prev - keysArray.length));
      setSelectedKeys(new Set());

      toast.success(`Đã xóa ${keysArray.length.toLocaleString('vi-VN')} từ đã chọn thành công!`);

      if (workerRef.current) {
        workerRef.current.postMessage({
          type: 'SYNC_INCREMENTAL',
          dictType,
          action: 'batch_delete',
          entryKeys: keysArray,
        });
      }
    } catch (err: any) {
      toast.error(`Xóa hàng loạt thất bại: ${err.message || 'Lỗi không xác định'}`);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Save entry (Add / Edit)
  const handleSaveEntry = async (entry: DictionaryEntry) => {
    try {
      await upsertSingleEntry(entry);
      const updatedCount = await countEntriesByType(dictType);
      await updateMetadata({
        type: dictType,
        wordCount: updatedCount,
        updatedAt: Date.now(),
      });

      setTotalWordCount(updatedCount);
      toast.success(`Đã lưu từ "${entry.key}" thành công!`);

      if (workerRef.current) {
        workerRef.current.postMessage({
          type: 'SYNC_INCREMENTAL',
          dictType,
          action: 'upsert',
          entryKey: entry.key,
          entry,
        });

        workerRef.current.postMessage({
          type: 'SEARCH',
          dictType,
          query: debouncedQuery,
          isRegex,
          isCaseSensitive,
          page: 1,
          pageSize: 300,
        });
      }
    } catch (err: any) {
      toast.error(`Lưu thất bại: ${err.message || 'Lỗi không xác định'}`);
    }
  };

  const isSearching = debouncedQuery.trim().length > 0;
  const formattedMatchCount = totalMatched.toLocaleString('vi-VN');
  const formattedTotalCount = totalWordCount.toLocaleString('vi-VN');
  const isAllSelected = displayedEntries.length > 0 && selectedKeys.size >= displayedEntries.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)' }}>
      {/* Header Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginBottom: '0.75rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={onBack}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.375rem 0.625rem',
              borderRadius: '0.375rem',
              backgroundColor: '#ffffff',
              border: '1px solid var(--border-light)',
              fontSize: '0.8125rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={16} />
            <span>Quay Lại</span>
          </button>

          <h2 style={{ fontSize: '1.125rem', fontWeight: 700, margin: 0, color: 'var(--text-main)', whiteSpace: 'nowrap' }}>
            Từ điển: <span style={{ color: '#0284c7' }}>{dictType}</span>
          </h2>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            ({isSearching ? `${formattedMatchCount} / ` : ''}{formattedTotalCount} từ)
          </span>

          {/* Speed & Performance Badge */}
          {searchTimeMs !== null && (
            <div
              title="Tốc độ xử lý từ Worker RAM (V8 / RE2JS Engine)"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.125rem 0.5rem',
                borderRadius: '9999px',
                backgroundColor: '#ecfdf5',
                color: '#059669',
                fontSize: '0.75rem',
                fontWeight: 700,
                border: '1px solid rgba(16, 185, 129, 0.2)',
                flexShrink: 0,
              }}
            >
              <Zap size={12} fill="#059669" />
              <span>{searchTimeMs}ms</span>
            </div>
          )}
        </div>
      </div>

      {/* Unified Search Input with Embedded Toggles */}
      <div style={{ marginBottom: '0.75rem' }}>
        <div style={{ position: 'relative', width: '100%' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Tìm kiếm từ khóa (tất cả / regex)..."
            style={{
              width: '100%',
              padding: '0.625rem 5.5rem 0.625rem 2.25rem',
              borderRadius: '0.5rem',
              border: '1px solid var(--border-light)',
              fontSize: '0.875rem',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: '0.375rem',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: '0.25rem',
            }}
          >
            {/* Toggle Case Sensitivity (Aa) */}
            <button
              onClick={() => setIsCaseSensitive(!isCaseSensitive)}
              title="Phân biệt hoa/thường (Aa)"
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '0.25rem',
                border: '1px solid var(--border-light)',
                backgroundColor: isCaseSensitive ? 'var(--accent-highlight)' : '#f1f5f9',
                color: 'var(--text-on-light)',
                fontWeight: 700,
                fontSize: '0.75rem',
                cursor: 'pointer',
              }}
            >
              Aa
            </button>

            {/* Toggle Regex (.*) */}
            <button
              onClick={() => setIsRegex(!isRegex)}
              title="Tìm kiếm bằng RE2JS Regular Expression (.*)"
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '0.25rem',
                border: '1px solid var(--border-light)',
                backgroundColor: isRegex ? 'var(--accent-highlight)' : '#f1f5f9',
                color: 'var(--text-on-light)',
                fontWeight: 700,
                fontSize: '0.75rem',
                cursor: 'pointer',
              }}
            >
              .*
            </button>
          </div>
        </div>
      </div>

      {/* Sub Controls: Select All & Batch Delete */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
        }}
      >
        <button
          onClick={handleToggleSelectAll}
          disabled={displayedEntries.length === 0}
          title={isAllSelected ? 'Bỏ chọn tất cả' : 'Chọn tất cả từ hiển thị'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.375rem 0.625rem',
            borderRadius: '0.375rem',
            border: '1px solid var(--border-light)',
            backgroundColor: isAllSelected ? 'var(--accent-highlight)' : '#ffffff',
            color: 'var(--text-on-light)',
            fontWeight: 600,
            fontSize: '0.8125rem',
          }}
        >
          {isAllSelected ? <CheckSquare size={16} /> : <Square size={16} />}
          <span>{isAllSelected ? 'Bỏ chọn' : 'Chọn tất cả'}</span>
        </button>
      </div>

      {/* Bulk Delete Bar */}
      {selectedKeys.size > 0 && (
        <div
          style={{
            backgroundColor: '#fff1f2',
            border: '1px solid rgba(228, 59, 18, 0.3)',
            borderRadius: '0.375rem',
            padding: '0.625rem 1rem',
            marginBottom: '0.75rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--action-danger)' }}>
            Đã chọn {selectedKeys.size.toLocaleString('vi-VN')} từ
          </span>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setSelectedKeys(new Set())}
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '0.25rem',
                border: '1px solid var(--border-light)',
                backgroundColor: '#ffffff',
                fontSize: '0.8125rem',
              }}
            >
              Bỏ chọn
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="btn-danger"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                fontSize: '0.8125rem',
                padding: '0.375rem 0.75rem',
              }}
            >
              {isBulkDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              <span>Xóa {selectedKeys.size.toLocaleString('vi-VN')} từ đã chọn</span>
            </button>
          </div>
        </div>
      )}

      {regexError && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            color: 'var(--action-danger)',
            fontSize: '0.8125rem',
            marginBottom: '0.75rem',
          }}
        >
          <AlertCircle size={16} />
          <span>{regexError}</span>
        </div>
      )}

      {/* List Container with Virtual Scroll (virtua) */}
      <div
        style={{
          flex: 1,
          backgroundColor: '#ffffff',
          borderRadius: '0.5rem',
          border: '1px solid var(--border-light)',
          overflow: 'hidden',
        }}
      >
        {loading && displayedEntries.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--text-muted)', gap: '0.5rem' }}>
            <Loader2 size={20} className="animate-spin" />
            <span>Đang tải dữ liệu từ điển...</span>
          </div>
        ) : displayedEntries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            {searchQuery ? 'Không tìm thấy từ khớp yêu cầu.' : 'Từ điển chưa có từ nào. Bấm nút (+) để thêm.'}
          </div>
        ) : (
          <VList
            style={{ height: '100%' }}
            onRangeChange={(_start, end) => {
              if (end >= displayedEntries.length - 20 && hasMore && !loading) {
                handleLoadMore();
              }
            }}
          >
            {displayedEntries.map((item) => {
              const isSelected = selectedKeys.has(item.key);

              return (
                <div
                  key={item.key}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.625rem 1rem',
                    borderBottom: '1px solid var(--border-light)',
                    fontSize: '0.875rem',
                    backgroundColor: isSelected ? 'var(--bg-card)' : 'transparent',
                  }}
                >
                  <button
                    onClick={() => toggleSelectKey(item.key)}
                    style={{ paddingRight: '0.625rem', color: isSelected ? 'var(--action-primary)' : 'var(--text-muted)' }}
                  >
                    {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                  </button>

                  <div style={{ flex: 1, minWidth: 0, paddingRight: '0.5rem' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{item.key}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                      {item.meanings.length > 0 ? item.meanings.join(' | ') : <em style={{ color: '#94a3b8' }}>(Nghĩa rỗng)</em>}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.375rem' }}>
                    <button
                      onClick={() => {
                        setEditingEntry(item);
                        setIsModalOpen(true);
                      }}
                      title="Sửa từ"
                      style={{ color: 'var(--action-primary)', padding: '0.25rem' }}
                    >
                      <Edit2 size={16} />
                    </button>

                    <button
                      onClick={() => handleDeleteEntry(item.key)}
                      title="Xóa từ lập tức"
                      style={{ color: 'var(--action-danger)', padding: '0.25rem' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </VList>
        )}
      </div>

      {/* Floating Action Button (FAB) */}
      <button
        onClick={() => {
          setEditingEntry(null);
          setIsModalOpen(true);
        }}
        title="Thêm từ mới"
        style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          width: '3.5rem',
          height: '3.5rem',
          borderRadius: '50%',
          backgroundColor: 'var(--action-primary)',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(128, 198, 249, 0.4)',
          border: 'none',
          zIndex: 900,
        }}
      >
        <Plus size={28} />
      </button>

      {/* Add / Edit Entry Modal */}
      <EntryModal
        dictType={dictType}
        initialEntry={editingEntry}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingEntry(null);
        }}
        onSave={handleSaveEntry}
      />
    </div>
  );
};
